import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import {
  Drawer,
  DrawerContent,
  DrawerHeader,
  DrawerTitle,
  DrawerDescription,
  DrawerFooter,
} from "@/components/ui/drawer";
import { Button } from "@/components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Batch } from "@/types/cbs";
import { CheckCircle2, XCircle } from "lucide-react";

const ABANDON_REASONS = [
  { value: "feed_shortage", label: "Feed Shortage" },
  { value: "disease_outbreak", label: "Disease Outbreak" },
  { value: "financial_constraints", label: "Financial Constraints" },
  { value: "natural_disaster", label: "Natural Disaster" },
  { value: "market_conditions", label: "Market Conditions" },
  { value: "other", label: "Other" },
] as const;

export type EndBatchAction = "complete" | "abandon";

interface EndBatchSheetProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  batch: Batch;
  onConfirm: (action: EndBatchAction, reason?: string) => void;
}

export const EndBatchSheet: React.FC<EndBatchSheetProps> = ({
  open,
  onOpenChange,
  batch,
  onConfirm,
}) => {
  const { t } = useTranslation();
  const [abandonReason, setAbandonReason] = useState<string>("");

  const batchDay = Math.max(
    1,
    Math.floor(
      (Date.now() - new Date(batch.placement_date).getTime()) / 86400000
    ) + 1
  );

  const liveBirds =
    (batch.current_bird_count ?? batch.doc_count) -
    (batch.cumulative_mortality ?? 0);

  return (
    <Drawer open={open} onOpenChange={onOpenChange}>
      <DrawerContent>
        <DrawerHeader>
          <DrawerTitle>{t("cbs.batch.endBatch", { defaultValue: "End Batch" })}</DrawerTitle>
          <DrawerDescription>
            {batch.batch_code ?? batch.id.slice(0, 8)}
          </DrawerDescription>
        </DrawerHeader>

        <div className="px-4 space-y-4">
          {/* Batch summary */}
          <div className="grid grid-cols-2 gap-3">
            <SummaryItem label="Day" value={`${batchDay} / ${batch.planned_cycle_days}`} />
            <SummaryItem label="Live Birds" value={liveBirds.toLocaleString()} />
            <SummaryItem label="FCR" value={batch.current_fcr?.toFixed(2) ?? "—"} />
            <SummaryItem label="Feed (kg)" value={batch.cumulative_feed_kg?.toLocaleString() ?? "—"} />
          </div>

          {/* Abandon reason picker */}
          <div className="space-y-2">
            <label className="text-sm font-medium text-muted-foreground">
              {t("cbs.batch.abandonReason", { defaultValue: "Reason for abandoning (if applicable)" })}
            </label>
            <Select value={abandonReason} onValueChange={setAbandonReason}>
              <SelectTrigger>
                <SelectValue placeholder="Select reason..." />
              </SelectTrigger>
              <SelectContent>
                {ABANDON_REASONS.map((r) => (
                  <SelectItem key={r.value} value={r.value}>
                    {r.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>

        <DrawerFooter>
          <Button
            className="w-full bg-success hover:bg-success/90 text-success-foreground"
            onClick={() => onConfirm("complete")}
          >
            <CheckCircle2 className="w-5 h-5 mr-2" />
            {t("cbs.batch.completeBatch", { defaultValue: "Complete Batch" })}
          </Button>
          <Button
            variant="destructive"
            className="w-full"
            disabled={!abandonReason}
            onClick={() => onConfirm("abandon", abandonReason)}
          >
            <XCircle className="w-5 h-5 mr-2" />
            {t("cbs.batch.abandonBatch", { defaultValue: "Abandon Batch" })}
          </Button>
        </DrawerFooter>
      </DrawerContent>
    </Drawer>
  );
};

const SummaryItem: React.FC<{ label: string; value: string }> = ({ label, value }) => (
  <div className="rounded-xl bg-muted/50 p-3 text-center">
    <div className="text-xs text-muted-foreground">{label}</div>
    <div className="text-lg font-bold text-foreground">{value}</div>
  </div>
);
